import styles from './InvoiceDetailsExport.module.css';

import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { buildProjectionItemsColumns } from './ProjectionDetailsDialog';
import useFetchData from 'components/hooks/useFetchData';
import useBimContext from 'components/hooks/useBimContext';
import Api from 'components/Api';
import { proposalUtils } from 'components/service/bng/AccountApi';
import BngTable from 'components/bng/ui/BngTable';
import Utils from 'components/Utils';
import useTranslation from 'components/hooks/useTranslation';

export const Situation = {
  COMMITTED: 'open',
  PAID: 'paid',
  ACTIVE: 'projected',
  UPFRONT: 'upfront',
};

const invoiceProjectionItemPriority = (item) => {
  const pricing = item.invoiceItem?.pricing ?? item.pricing;
  const isVoucher = item.type === 'VOUCHER';
  const voucherType = item.props?.voucherType;

  if (pricing === 0 || (isVoucher && voucherType === 'BONUS')) {
    return 2;
  }

  if (pricing < 0 || (isVoucher && voucherType === 'DISCOUNT')) {
    return 1;
  }

  return 0;
};

export default function InvoiceDetailsExport() {
  const context = useBimContext();
  const { t } = useTranslation();
  let { accountId, invoiceProjectionId } = useParams();

  accountId = parseInt(accountId);
  invoiceProjectionId = parseInt(invoiceProjectionId);

  const $projectionAndPayments = useFetchData(async () => {
    if (!accountId || !invoiceProjectionId) {
      return null;
    }

    const projection = await Api.Account.findInvoiceProjectionInfo(accountId, invoiceProjectionId);

    const invoiceItems = projection.invoiceProjection.invoice?.invoiceItems;
    if (invoiceItems) {
      projection.invoiceProjectionItems.forEach((invoiceProjection) => {
        invoiceProjection.invoiceItem = invoiceItems.find(
          (item) => item.invoiceProjectionItemId === invoiceProjection.id
        );
      });
    }

    let payments = [];
    const invoiceId = projection.invoiceProjection.invoice?.id;

    if (invoiceId) {
      payments = await Api.Account.fetchPaymentsForInvoice(accountId, invoiceId);
    }

    return {
      projection,
      payments,
    };
  }, [accountId, invoiceProjectionId]);

  const projection = $projectionAndPayments.data?.projection;
  const payments = $projectionAndPayments.data?.payments;
  const invoiceProjection = projection?.invoiceProjection;
  const contract = projection?.invoiceProjection.contract;
  const currencySymbol = contract?.currency.symbol;

  const cols = useMemo(() => buildProjectionItemsColumns(context, currencySymbol, true), [currencySymbol]);

  const paymentDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(window.__USER_LANG || 'pt-BR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    []
  );

  const formattedProjectionDate = useMemo(() => {
    const contractDate =
      invoiceProjection?.type === 'PARTIAL'
        ? new Date(`${projection?.invoiceProjectionItems[0].partialPaymentDate}T12:00:00`)
        : new Date(`${invoiceProjection?.referenceMonth}T12:00:00`);
    const startingDate =
      invoiceProjection?.type === 'PARTIAL'
        ? contractDate
        : new Date(contractDate.getFullYear(), contractDate.getMonth(), 1);
    const lastDayOfMonth = new Date(contractDate.getFullYear(), contractDate.getMonth() + 1, 0);

    return `${Utils.Date.formatDate(startingDate)} -> ${Utils.Date.formatDate(lastDayOfMonth)}`;
  }, [projection, invoiceProjection]);

  const { totalPricing, discounts, paidValue } = useMemo(() => {
    let total = 0;
    let discount = 0;
    let paid = 0;

    projection?.invoiceProjectionItems.forEach((item) => {
      const value = item.pricing ?? 0;
      if (value > 0) {
        total += value;
      } else {
        discount += value;
      }

      if (item.paid) {
        paid += value;
      }
    });

    return { totalPricing: total, discounts: discount, paidValue: paid };
  }, [projection]);

  const sortedItems = useMemo(() => {
    return projection?.invoiceProjectionItems?.slice().sort((a, b) => {
      const a1 = invoiceProjectionItemPriority(a);
      const b1 = invoiceProjectionItemPriority(b);
      return a1 - b1;
    });
  }, [projection?.invoiceProjectionItems]);

  const generatedDate = paymentDateFormatter.format(new Date());

  const statusLabel = Situation[projection?.invoiceProjection?.status];

  return (
    <>
      {projection && (
        <div className={`InvoiceDetailsExport ${styles.bodyWrapper}`}>
          <div className="flex-1">
            <div className="d-flex jc-se">
              <div className={styles.infoContainer}>
                <img
                  alt="BIM Logo"
                  className="mb-2"
                  width="150"
                  src={`${Api.baseUrl()}/resources/images/email_logo_new.png`}
                />
                <p>{t('bim.corporate.name')}</p>
                <p>07.780.779/0001-54</p>
                <p>{t('bim.address')}</p>
                <p>{t('bim.city')}</p>
                <p>{t('bim.telephone.number')}</p>
              </div>
              <div className={styles.infoContainer}>
                <h4>{t('invoice')}</h4>
                <div>
                  <span className={styles.grayLabels}>{`${t('number')}: `}</span>
                  <span>{invoiceProjection.id}</span>
                </div>
                <div>
                  <span className={styles.grayLabels}>{`${t('reference.date')}: `}</span>
                  <span>{Utils.Date.formatDate(new Date(`${invoiceProjection.referenceMonth}T12:00:00`))}</span>
                </div>
                <div>
                  <span className={styles.grayLabels}>{`${t('PERIOD')}: `}</span>
                  <span>{formattedProjectionDate}</span>
                </div>
                <div>
                  <span className={styles.grayLabels}>{`${t('payment.format')}: `}</span>
                  <span>{t(`paymentFormats.${contract.paymentFormat}`)}</span>
                </div>
              </div>
            </div>
            <div className="d-flex mt-3">
              <div className={styles.infoContainer}>
                <p className={styles.grayLabels}>{t('charge.from')}</p>
                <h4>{projection.invoiceProjection.payer.corporateName}</h4>
                <p>{invoiceProjection.payer.emails}</p>
                <p>
                  {`${
                    invoiceProjection.payer.city.charAt(0).toUpperCase() +
                    invoiceProjection.payer.city.slice(1).toLowerCase()
                  }, ${invoiceProjection.payer.state} - ${invoiceProjection.payer.country}`}
                </p>
                <p>
                  {t('contract')} {contract.contractNumber ?? contract.id}
                </p>
              </div>
              <div className={`${styles.infoContainer} mt-2`}>
                <div className="d-flex" style={{ lineHeight: '30px' }}>
                  <div className={`${styles.statusLabel} ${styles[statusLabel]} w-50`}>
                    <span>{t(`situation.${statusLabel}`)}</span>
                  </div>
                  <div className={`${styles.pricingCells} ${styles.paymentDateCell}`}>{generatedDate}</div>
                </div>
                <div className={`${styles.totalPricing} ${styles.pricingCells}`}>
                  {proposalUtils.formatPricing(invoiceProjection.nominalPricing, currencySymbol)}
                </div>
              </div>
            </div>
            <div>
              <div className={styles.projectionItemsTableWrapper}>
                <BngTable rows={sortedItems} cols={cols} stickyHeader />
              </div>
              <div className="d-flex flex-direction-column">
                <div className={`${styles.totalPricingWrapper} ${styles.totalPricingBold}`}>
                  <span>{`${t('total')}:`}</span>
                  <span className={styles.pricingLabels}>
                    {proposalUtils.formatPricing(totalPricing, currencySymbol)}
                  </span>
                </div>
                <div className={styles.totalPricingWrapper}>
                  <span>{`${t('discounts')}:`}</span>
                  <span className={styles.pricingLabels}>{proposalUtils.formatPricing(discounts, currencySymbol)}</span>
                </div>
                <div className={styles.totalPricingWrapper}>
                  <span>{`${t('paid.amount')}:`}</span>
                  <span className={styles.pricingLabels}>
                    {proposalUtils.formatPricing(paidValue === 0 ? paidValue : `-${paidValue}`, currencySymbol)}
                  </span>
                </div>
                <hr className={styles.pricingSeparator} width="300" />
                <div className={`${styles.totalPricingWrapper} ${styles.totalPricingBold} text-lg`}>
                  <span>{`${t('unpaid.amount')}:`}</span>
                  <span className={styles.pricingLabels}>
                    {proposalUtils.formatPricing(totalPricing + discounts - paidValue, currencySymbol)}
                  </span>
                </div>
              </div>
            </div>
            {payments.length > 0 && (
              <div style={{ margin: '20px 0' }}>
                <p className="mb-1">{`${t('payments')}:`}</p>
                {payments.map((p) => {
                  return (
                    <div key={p.id} style={{ fontStyle: 'italic' }}>
                      {p.createdAt && <span>{new Date(p.createdAt).toLocaleDateString()}</span>}
                      <span className="ml-2">{proposalUtils.formatPricing(p.amount, currencySymbol)}</span>
                      <span className="ml-2">{t(`paymentFormats.${contract.paymentFormat}`)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className={styles.pageFooter} dangerouslySetInnerHTML={{ __html: t('invoice.export.footer') }} />
        </div>
      )}
    </>
  );
}
